import { AssessmentResult } from '../types/assessment'

export function calculateResults(
  forcedAnswers: number[],
  scaleAnswers: number[],
): AssessmentResult {
  // 1. Calculate Raw Scores
  const forced_score = forcedAnswers.reduce((sum, val) => sum + val, 0)

  // Scale answers are 1-5.
  // First 5 are Drive, next 5 are DI, last 5 are SC
  const drive_answers = scaleAnswers.slice(0, 5)
  const di_answers = scaleAnswers.slice(5, 10)
  const sc_answers = scaleAnswers.slice(10, 15)

  const drive_score = drive_answers.reduce((sum, val) => sum + val, 0)
  const di_score = di_answers.reduce((sum, val) => sum + val, 0)
  const sc_score = sc_answers.reduce((sum, val) => sum + val, 0)

  // Calculate Percentages (Score range 5-25 -> 0-100%)
  const drive_pct = Math.round(((drive_score - 5) / 20) * 100)
  const di_pct = Math.round(((di_score - 5) / 20) * 100)
  const sc_pct = Math.round(((sc_score - 5) / 20) * 100)

  // 2. Gaming Detection
  let possible_gaming = false
  const gaming_reasons: string[] = []

  // Rule 1: All scores very high (The "Superhuman" check)
  if (drive_score >= 22 && di_score >= 22 && sc_score >= 22) {
    possible_gaming = true
    gaming_reasons.push('All scores suspiciously high (>85%)')
  }

  // Rule 2: Low forced score (honest/passive) but high self-rated drive (inflated)
  if (forced_score <= 4 && drive_score >= 20) {
    possible_gaming = true
    gaming_reasons.push(
      'Contradiction: Low forced-choice drive vs. high self-rating',
    )
  }

  // Rule 3: Straight-lining (Variance Check)
  // Calculate variance of all scale answers
  const mean = scaleAnswers.reduce((a, b) => a + b, 0) / scaleAnswers.length
  const variance =
    scaleAnswers.reduce((a, b) => a + Math.pow(b - mean, 2), 0) /
    scaleAnswers.length

  if (variance < 0.25) {
    possible_gaming = true
    gaming_reasons.push('Straight-lining detected (identical answers)')
  } else if (variance < 0.6) {
    possible_gaming = true
    gaming_reasons.push('Low variance detected (patterned responses)')
  }

  // Rule 4: Forced Choice Straight-lining
  if (forced_score === 0 || forced_score === 10) {
    // Not necessarily gaming, but worth noting if combined with other factors
    // We'll just flag it as a reason if gaming is already suspected, or strict check
    if (variance < 1.0) {
      possible_gaming = true
      gaming_reasons.push('Patterned responses across both sections')
    }
  }

  // 3. Confidence Level
  let confidence_level: 'High' | 'Moderate' | 'Low'
  if (forced_score >= 7) {
    confidence_level = 'High'
  } else if (forced_score >= 5) {
    confidence_level = 'Moderate'
  } else {
    confidence_level = 'Low'
  }

  // 4. Profile Classification
  let final_profile = 'Review Manually'

  // Logic order is critical - first match wins

  // Check 1: At-Risk / Mismatch
  if (drive_score < 12 || forced_score <= 4) {
    final_profile = 'At-Risk'
  }
  // Check 2: Hunter (Ideal D2D Rep)
  else if (
    drive_score >= 20 &&
    di_score >= 18 &&
    sc_score <= 15 &&
    forced_score >= 7
  ) {
    final_profile = 'Hunter'
  }
  // Check 3: Closer
  else if (
    drive_score >= 18 &&
    di_score >= 15 &&
    sc_score >= 12 &&
    sc_score <= 18 &&
    forced_score >= 6
  ) {
    final_profile = 'Closer'
  }
  // Check 4: Steady Performer
  else if (drive_score >= 12 && drive_score <= 18 && sc_score >= 18) {
    final_profile = 'Steady Performer'
  }

  return {
    forced_score,
    drive_score,
    di_score,
    sc_score,
    drive_pct,
    di_pct,
    sc_pct,
    possible_gaming,
    gaming_reasons,
    confidence_level,
    final_profile,
  }
}

export function getProfileDescription(profile: string): string {
  switch (profile) {
    case 'Hunter':
      return "You're competitive, resilient, and comfortable taking initiative. You perform best in fast-paced environments where effort and results are clearly connected."
    case 'Closer':
      return 'You have a strong blend of drive and social influence. You excel at building relationships quickly and guiding others toward a decision.'
    case 'Steady Performer':
      return 'You are reliable, consistent, and detail-oriented. You build trust through dependability and excel in roles requiring long-term relationship management.'
    case 'At-Risk':
      return 'Your current preferences suggest you may find the high-rejection nature of direct sales challenging. You might thrive better in support or operational roles.'
    default:
      return "Your profile shows a unique mix of traits. We'd like to discuss your results personally to understand your ideal work environment."
  }
}
